import React, { useEffect, useState } from 'react';
import {
  ThemeProvider,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';

import { theme } from '../../Medical_History_Forms/themeprovider';
import { useLoader } from '../../loaderProvider/loader';
import { defaultRetrievalData, getOocyteRetrieval, MasterconfigrutationDetails } from './OocyteRetrival.helper';
import { infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { formatDate } from '../../../utils/dateUtils';

interface OocyteRetrievalDetailsProps {
  cycleId: number | null;
  dropDownOptions?: MasterconfigrutationDetails;
}
const renderField = (
  label: string,
  value: string | number | undefined | null
) => {
  const isEmpty = value === undefined || value === null || value === '';
  if (isEmpty) return null;

  return (
    <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
      <Typography variant="body1" sx={sectionTitleStyle}>
        {label}
      </Typography>
      <Typography variant="body2" sx={infoTextStyle}>
        {value}
      </Typography>
    </Grid2>
  );
};

const OocyteRetrievalDetails: React.FC<OocyteRetrievalDetailsProps> = ({ cycleId }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const { showLoader, hideLoader } = useLoader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [retrievalData, setRetrievalData] = useState<any>(null);

  useEffect(() => {
    if (!cycleId) return;

    showLoader();
    getOocyteRetrieval(cycleId)
      .then(data => setRetrievalData(data))
      .catch(() => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch oocyte retrieval data');
        setRetrievalData(defaultRetrievalData);
      })
      .finally(() => hideLoader());
  }, [cycleId, showLoader, hideLoader]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid2 container spacing={4} mb={3}>
          {renderField(tGeneral('cycleStartDate'), formatDate(retrievalData?.cycle_start_date))}
          {renderField(tSurgery('opuDate'), formatDate(retrievalData?.opu_date))}
          {renderField(tGeneral('startTime'), retrievalData?.start_time)}
          {renderField(tGeneral('endTime'), retrievalData?.end_time)}
          {renderField(tGeneral('embryologist'), retrievalData?.embryologist_name)}
          {renderField(tGeneral('clinician'), retrievalData?.clinician_name)}
          {renderField(tGeneral('witnessEmbryologist'), retrievalData?.witness_embryologist_name)}
          {renderField(tGeneral('anesthetist'), retrievalData?.anesthetist_name)}
          {renderField(
            tGeneral('typeOfAnesthesia'), retrievalData?.type_of_anesthesia_label
          )}
          {renderField(tGeneral('noOfNeedleUsed'), retrievalData?.no_of_needles_used)}
          {renderField(tGeneral('nurse'), retrievalData?.nurse_name)}
          {renderField(tGeneral('tubeId'), retrievalData?.tube_id)}
          {renderField(
            tGeneral('typeOfNeedle'), retrievalData?.type_of_needle_label
          )}
          {renderField(
            tGeneral('subTypeOfNeedle'), retrievalData?.sub_type_of_needle_label
          )}
          {renderField(
            tGeneral('levelOfDifficulty'), retrievalData?.difficulty_level_label
          )}
          {renderField(tGeneral('procedureFindings'), retrievalData?.procedure_findings)}
          {renderField(tGeneral('optimumFolliclesLeft'), retrievalData?.optimum_follicles?.left_follicles)}
          {renderField(tGeneral('optimumFolliclesRight'), retrievalData?.optimum_follicles?.right_follicles)}
          {renderField(
            tGeneral('oocyteRetrievedLeft'), retrievalData?.oocytes_retrieved_left
          )}
          {renderField(
            tGeneral('oocyteRetrievedRight'), retrievalData?.oocytes_retrieved_right
          )}
          {renderField(tGeneral('notes'), retrievalData?.notes)}

          <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={retrievalData?.pre_anesthesia_checkup === true}
                  icon={<span style={{ color: 'red', fontSize: '1rem' }}>✖</span>}
                  checkedIcon={<span style={{ color: 'green', fontSize: '1rem' }}>✔</span>}
                  readOnly
                />
              }
              label={tGeneral('preAnesthesiaCheckup')}
            />
          </Grid2>
        </Grid2>
      </Box>
    </ThemeProvider>
  );
};

export default OocyteRetrievalDetails;

